import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Wrench, Clock, Bell, ArrowRight, Phone, Mail, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ServiceDepannage() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const notify = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Adresse email invalide");
      return;
    }
    setBusy(true);
    try {
      await api.post("/waitlist", { email, service: "depannage", source: "depannage-page" });
      setSubmitted(true);
      toast.success("Nous vous préviendrons dès l'ouverture du service.");
    } catch (err) {
      toast.error("Impossible d'enregistrer votre email pour le moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="service-depannage-page">
      <Header />

      {/* HERO — bientôt disponible */}
      <section
        className="relative pt-40 pb-32 overflow-hidden grain"
        style={{
          background:
            "radial-gradient(ellipse at 20% 40%, rgba(212,175,55,0.10), transparent 55%), radial-gradient(ellipse at 80% 60%, rgba(212,175,55,0.06), transparent 60%), #050505",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center reveal">
          <div className="inline-flex items-center gap-2 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/[0.05] gold px-4 py-2 text-[10px] tracking-[0.4em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--gold)] animate-pulse" />
            Bientôt disponible
          </div>
          <h1 className="mt-8 font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Service <span className="gold">Dépannage</span><br />
            en préparation
          </h1>
          <p className="mt-8 text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Nous préparons un service complet de dépannage et remorquage, disponible 24h/24 et 7j/7,
            pour venir à votre secours partout où vous êtes.
          </p>

          {/* Icon showcase */}
          <div className="mt-14 grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { icon: Wrench, title: "Dépannage sur place", text: "Panne de batterie, crevaison, essence, clés perdues." },
              { icon: Clock, title: "24h / 24 – 7j / 7", text: "Intervention rapide de jour comme de nuit." },
              { icon: Bell, title: "Remorquage", text: "Vers votre garage habituel ou celui de votre choix." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-6 border border-white/10 bg-black/40 backdrop-blur-sm text-left"
              >
                <Icon size={22} className="gold mb-3" />
                <div className="font-serif text-lg text-white">{title}</div>
                <p className="text-sm text-gray-400 mt-1 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waiting list */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs tracking-[0.5em] gold uppercase">Soyez averti</div>
          <h2 className="mt-3 font-serif text-white text-3xl sm:text-4xl">
            Prévenez-moi à l'ouverture du service.
          </h2>
          <p className="mt-3 text-gray-400">
            Laissez votre email — nous vous prévenons personnellement dès que le
            service dépannage est en ligne.
          </p>

          {submitted ? (
            <div
              data-testid="depannage-waitlist-success"
              className="mt-8 inline-flex items-center gap-3 border border-[color:var(--gold)]/40 bg-[color:var(--gold)]/[0.05] gold px-6 py-4"
            >
              <CheckCircle2 size={18} />
              Nous vous préviendrons à l'adresse indiquée.
            </div>
          ) : (
            <form onSubmit={notify} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                data-testid="depannage-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                className="flex-1 bg-black border border-white/10 focus:border-[color:var(--gold)] px-4 py-3 text-white focus:outline-none"
                required
              />
              <button
                data-testid="depannage-notify-btn"
                type="submit"
                disabled={busy}
                className="btn-gold inline-flex items-center justify-center gap-2"
              >
                {busy ? "..." : (<>Me prévenir <ArrowRight size={16} /></>)}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Meanwhile */}
      <section className="py-20 border-t border-white/5 bg-[#080808]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs tracking-[0.5em] gold uppercase">En attendant</div>
          <h3 className="mt-3 font-serif text-white text-3xl mb-8">
            Besoin d'aide dès maintenant ?
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href={`tel:${(settings?.phone || "0645142965").replace(/\s+/g, "")}`}
              className="border border-white/10 bg-black hover:border-[color:var(--gold)]/60 p-6 group transition-colors"
              data-testid="depannage-call"
            >
              <Phone size={20} className="gold mb-3" />
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Appelez-nous</div>
              <div className="font-serif text-2xl text-white mt-1">
                {settings?.phone || "06 45 14 29 65"}
              </div>
            </a>
            <a
              href={`mailto:${settings?.email || "contact.dassimaoauto@gmail.com"}?subject=Demande de dépannage`}
              className="border border-white/10 bg-black hover:border-[color:var(--gold)]/60 p-6 group transition-colors"
              data-testid="depannage-mail"
            >
              <Mail size={20} className="gold mb-3" />
              <div className="text-[10px] uppercase tracking-widest text-gray-500">Écrivez-nous</div>
              <div className="text-white mt-1 break-all">
                {settings?.email || "contact.dassimaoauto@gmail.com"}
              </div>
            </a>
          </div>

          <div className="mt-14">
            <p className="text-sm text-gray-500 mb-4">
              Vous cherchez à convoyer un véhicule ou réserver un chauffeur ?
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/services/convoyage" className="btn-outline-gold">
                Découvrir le Convoyage
              </Link>
              <Link to="/services/vtc" className="btn-outline-gold">
                Découvrir le VTC
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
